<template>
    <b-modal id="campaign-rule-set-preset-modal"
             title="캠페인 규칙셋 프리셋 등록"
             size="xl"
             scrollable
             hide-footer
             modal-class="default-modal preset-modal"
             no-close-on-backdrop
             :no-close-on-esc="submitting"
             :hide-header-close="submitting"
             @show="onShow"
             @hide="onHide"
    >
        <div class="preset-actionbar d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center" style="gap:8px;">
                <span class="font-weight-bold text-nowrap">프리셋 유형</span>
                <b-form-select v-model="presetType"
                               :options="presetTypeOptions"
                               size="sm"
                               style="width:auto; min-width:160px;"
                               :disabled="submitting"
                />
            </div>
            <b-button variant="primary"
                      class="px-4"
                      style="min-width:170px;"
                      :disabled="submitting || (!hasFailures && !canSubmit)"
                      @click="onSubmit"
            >
                {{ generateButtonLabel }}
            </b-button>
        </div>

        <validation-observer ref="observer">
            <b-form id="campaign-rule-set-preset-form" autocomplete="new-password" @submit.stop.prevent>
                <dl class="description-list-wrapper">
                    <DescriptionItem label="프리셋명" mode="form" labelFor="preset-name" required>
                        <div class="w-100">
                            <validation-provider v-slot="vc" name="preset-name" :rules="{ required: true, min: 2 }">
                                <b-form-input id="preset-name"
                                              v-model="form.name"
                                              :disabled="submitting"
                                              placeholder="예: SEAH_S_26"
                                              :state="getValidationState(vc)"
                                              autocomplete="new-password"
                                />
                                <b-form-invalid-feedback>{{ vc.errors[0] }}</b-form-invalid-feedback>
                            </validation-provider>
                            <small class="text-muted">
                                규칙셋명은 <code>{프리셋명} - {기업규모}</code> 형식으로 생성됩니다.
                            </small>
                        </div>
                    </DescriptionItem>

                    <DescriptionItem label="연결 프로젝트" mode="form" labelFor="preset-project" required>
                        <div class="d-flex flex-column w-100">
                            <validation-provider v-slot="vc" name="preset-project" :rules="{ required: true }">
                                <!-- 한 번 생성하면 재시도가 같은 프로젝트로만 가도록 잠근다(다른 프로젝트는 창을 다시 열어서) -->
                                <auto-complete-select id="preset-project"
                                                      ref="projectAutoComplete"
                                                      v-model="selectedProjectCodeId"
                                                      class="w-100"
                                                      :options="projectCodeOptions"
                                                      start-expanded
                                                      list-max-height="360px"
                                                      option-value-key="id"
                                                      placeholder="프로젝트를 선택해주세요."
                                                      :disabled="submitting || hasRun"
                                                      :state="getValidationState(vc)"
                                                      autocomplete="new-password"
                                                      @click="vc.validate()"
                                />
                                <b-form-invalid-feedback class="d-block">{{ vc.errors[0] }}</b-form-invalid-feedback>
                            </validation-provider>
                        </div>
                    </DescriptionItem>

                    <DescriptionItem label="활성여부" mode="form" labelFor="preset-is-active" required>
                        <b-form-radio-group id="preset-is-active"
                                            v-model="form.is_active"
                                            :options="isActiveOption"
                                            :button-variant="form.is_active ? 'outline-success' : 'outline-secondary'"
                                            size="sm"
                                            buttons
                                            :disabled="submitting"
                        />
                    </DescriptionItem>

                    <DescriptionItem label="설명" mode="form" labelFor="preset-description">
                        <b-form-textarea id="preset-description"
                                         v-model="form.description"
                                         :disabled="submitting"
                                         placeholder="설명 (선택사항)"
                                         autocomplete="new-password"
                        />
                    </DescriptionItem>
                </dl>
            </b-form>
        </validation-observer>

        <div class="matching-card">
            <div class="matching-card-header d-flex align-items-center justify-content-between">
                <h6 class="mb-0">기업규모별 매칭</h6>
                <small class="text-muted">규칙그룹 = 자동 / 캠페인 = 직접 선택</small>
            </div>
            <div class="matching-card-body">
                <template v-if="loading">
            <div class="text-center py-4">
                <b-spinner variant="primary" small/>
                <span class="ml-2">기업 규모 규칙그룹을 불러오는 중...</span>
            </div>
        </template>

        <template v-else-if="rows.length === 0">
            <b-alert show variant="secondary" class="mb-0">
                '기업 규모' 규칙그룹이 없습니다. 먼저 [규칙 그룹 관리]에서 기업 규모(X) 그룹을 생성해주세요.
            </b-alert>
        </template>

        <template v-else>
            <b-alert :show="noCampaigns" variant="warning" class="py-1 px-2 mb-2">
                <small>캠페인 목록을 불러오지 못했습니다. 페이지를 새로고침하거나 개발팀에 문의해주세요.</small>
            </b-alert>
            <b-table fixed responsive :items="rows" :fields="matrixFields" class="mb-2">
                <template #cell(included)="{ item }">
                    <b-form-checkbox v-model="item.included"
                                     :disabled="submitting || item.result === 'success'"
                    />
                </template>
                <template #cell(ruleGroupName)="{ item }">
                    <span v-b-tooltip.hover="item.ruleGroupName ? { title: item.ruleGroupName } : null">{{ item.ruleGroupName }}</span>
                </template>
                <template #cell(campaign)="{ item }">
                    <auto-complete-select v-model="item.campaignId"
                                          :options="campaignOptions"
                                          list-max-height="280px"
                                          option-value-key="id"
                                          placeholder="캠페인을 선택해주세요."
                                          :disabled="!item.included || submitting || item.result === 'success'"
                                          autocomplete="new-password"
                    />
                    <small v-if="item.included && item.campaignId == null" class="text-danger d-block">
                        캠페인을 선택해주세요.
                    </small>
                    <small v-else-if="item.included && item.result == null && conflictReason(item)"
                           class="text-danger d-block"
                    >
                        {{ conflictReason(item) }}
                    </small>
                </template>
                <template #cell(result)="{ item }">
                    <b-badge v-if="item.result === 'success'" variant="success">성공</b-badge>
                    <b-badge v-else-if="item.result === 'conflict'"
                             v-b-tooltip.hover
                             variant="warning"
                             :title="conflictTitle(item)"
                    >중복</b-badge>
                    <b-badge v-else-if="item.result === 'error'"
                             v-b-tooltip.hover
                             variant="danger"
                             :title="item.errMessage || '생성 중 오류가 발생했습니다.'"
                    >실패</b-badge>
                    <span v-else-if="!item.included" class="text-muted">-</span>
                </template>
            </b-table>

            <b-alert :show="hasConflicts && !hasRun" variant="danger" class="py-1 px-2 mb-2">
                <small>이미 사용 중이거나 중복된 캠페인이 있어 생성할 수 없습니다. 아래 빨간 안내를 확인하고 캠페인을 변경(또는 해당 규모 포함 해제)해주세요.</small>
            </b-alert>

            <template v-if="!hasRun">
                <div class="text-muted mb-1"><small>생성 미리보기</small></div>
                <div class="preview-box">
                    <div v-for="row in includedRows" :key="`pv-${row.ruleGroupId}`" class="preview-row">
                        <code>{{ form.name ? `${form.name} - ${row.sizeLabel}` : `(프리셋명) - ${row.sizeLabel}` }}</code>
                        &rarr; {{ row.ruleGroupName }} / {{ campaignText(row.campaignId) || '(캠페인 미선택)' }}
                    </div>
                    <div v-if="includedRows.length === 0" class="text-muted">포함된 규모가 없습니다.</div>
                </div>
            </template>
                </template>
            </div>
        </div>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {createCampaignRuleSet, listCampaignRuleSets, listRuleGroups} from '@shared_utils/api/ruleEngine';
import DescriptionItem from '@shared/components/common/DescriptionItem.vue';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import {
    isActiveOption,
    RULE_GROUP_SIZE_SEARCH,
    extractSizeLabel
} from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/data';
import {
    getProjectCodeOptions,
    getCampaignOptions,
    getTargetCampaignText,
    getTargetProjectText
} from '@shared/components/admin/pages/Management/common-data';

export default {
    name: 'CampaignRuleSetPresetModal',
    components: {DescriptionItem, AutoCompleteSelect},
    props: {
        projects: {type: [Array, null], default: null},
        campaigns: {type: [Array, null], default: null},
    },
    data() {
        return {
            loading: false,
            submitting: false,
            hasRun: false,
            // 생성 진행률(이번 시도 대상 기준)
            progressDone: 0,
            progressTotal: 0,
            // 창을 다시 열거나 프로젝트를 바꾼 뒤 늦게 온 이전 조회 응답을 버리기 위한 순번
            showSeq: 0,
            existingSeq: 0,
            // 프리셋 유형 (확장 지점) — 현재 기업 규모만 지원
            presetType: 'company_size',
            selectedProjectCodeId: null,
            form: {name: null, description: null, is_active: true},
            // { ruleGroupId, ruleGroupName, sizeLabel, included, campaignId, result, createdId }
            rows: [],
            // 선택된 프로젝트의 기존 규칙셋: { [campaign_id]: { id, name } } — 사전 중복검사용
            existingByCampaign: {},
        };
    },
    computed: {
        isActiveOption() {
            return isActiveOption;
        },
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        campaignOptions() {
            return getCampaignOptions(this.campaigns);
        },
        includedRows() {
            return this.rows.filter(r => r.included);
        },
        canSubmit() {
            return !!this.form.name && this.form.name.length >= 2
                && this.selectedProjectCodeId != null
                && this.includedRows.length > 0
                && this.includedRows.every(r => r.campaignId != null)
                && !this.hasConflicts;
        },
        hasFailures() {
            // error 행만 재시도 대상 — conflict(이미 존재)는 종료 상태로 본다
            return this.hasRun && this.rows.some(r => r.included && r.result === 'error');
        },
        hasConflicts() {
            // 생성 전(pending) 포함 행 중 사전 중복(기존 사용/프리셋 내 중복)이 있으면 true
            return this.includedRows.some(r => r.result == null && !!this.conflictReason(r));
        },
        noCampaigns() {
            return !this.campaigns || this.campaigns.length === 0;
        },
        presetTypeOptions() {
            // 추후 프리셋 유형 추가 지점 (예: 업종별, 지역별 등)
            return [
                {value: 'company_size', text: '기업 규모'},
            ];
        },
        generateButtonLabel() {
            if (this.submitting) return this.progressTotal ? `생성 중... (${this.progressDone}/${this.progressTotal})` : '확인 중...';
            if (this.hasFailures) return '실패한 항목만 재시도';
            return `규칙셋 ${this.includedRows.length}개 생성`;
        },
        matrixFields() {
            const fields = [
                {key: 'included', label: '포함', thStyle: {width: '60px'}, thClass: ['th-center'], tdClass: ['td-center', 'align-middle']},
                {key: 'sizeLabel', label: '기업규모', thStyle: {width: '120px'}, thClass: ['th-left'], tdClass: ['td-left', 'align-middle']},
                {key: 'ruleGroupName', label: '연결 규칙 그룹', thClass: ['th-left'], tdClass: ['td-left', 'align-middle', 'text-truncate']},
                {key: 'campaign', label: '연결 캠페인', thStyle: {width: '340px'}, thClass: ['th-left'], tdClass: ['td-left', 'align-middle']},
            ];
            if (this.hasRun) {
                fields.push({key: 'result', label: '결과', thStyle: {width: '90px'}, thClass: ['th-center'], tdClass: ['td-center', 'align-middle']});
            }
            return fields;
        },
    },
    watch: {
        // onShow 스냅샷 이후 캠페인이 늦게 로드되면, 아직 미선택·미실행 행에 한해 자동매칭 복구
        campaigns(val) {
            if (!val) return;
            this.rows.forEach(r => {
                if (r.campaignId == null && r.result == null) {
                    this.$set(r, 'campaignId', this.autoMatchCampaignId(r.sizeLabel));
                }
            });
        },
        // 프로젝트 변경 시 해당 프로젝트의 기존 규칙셋을 불러와 캠페인 사전 중복검사 갱신
        selectedProjectCodeId(val) {
            this.fetchExistingRuleSets(val);
        },
    },
    methods: {
        getValidationState({dirty, validated, valid = null}) {
            return dirty || validated ? valid : null;
        },
        conflictTitle(row) {
            const d = row.errData;
            return (d && (d.suggested_action || (d.conflicts && d.conflicts[0] && d.conflicts[0].message)))
                || '이미 동일 조합의 규칙셋이 존재합니다.';
        },
        // 생성 전 사전 중복 사유 (없으면 null): 1) 기존 규칙셋에 이미 연결된 캠페인 2) 프리셋 내 중복
        conflictReason(row) {
            if (row.campaignId == null) return null;
            const existing = this.existingByCampaign[row.campaignId];
            if (existing) {
                return `이미 "${existing.name}" (#${existing.id}) 규칙셋에 연결된 캠페인입니다.`;
            }
            const dup = this.includedRows.filter(r => r.campaignId === row.campaignId).length;
            if (dup > 1) {
                return '프리셋 내 다른 기업규모와 캠페인이 중복됩니다.';
            }
            return null;
        },
        campaignText(campaignId) {
            return getTargetCampaignText(campaignId, this.campaigns);
        },
        async onShow() {
            this.resetAll();
            this.$nextTick(() => {
                if (this.$refs.observer) this.$refs.observer.reset();
            });
            await this.fetchSizeRuleGroups();
        },
        // 생성 중에는 닫지 않는다 — 닫으면 일부만 만들어진 채 '0개 생성' 으로 안내되고 목록도 갱신되지 않았다
        onHide(bvEvent) {
            if (this.submitting) {
                bvEvent.preventDefault();
                return;
            }
            this.resetAll();
        },
        resetAll() {
            this.form = {name: null, description: null, is_active: true};
            this.selectedProjectCodeId = null;
            this.rows = [];
            this.hasRun = false;
            this.submitting = false;
            this.progressDone = 0;
            this.progressTotal = 0;
            this.existingByCampaign = {};
        },
        autoMatchCampaignId(sizeLabel) {
            if (!this.campaigns || !sizeLabel) return null;
            const found = this.campaigns.find(c => (c.campaign_name || '').includes(sizeLabel));
            return found ? found.campaign_id : null;
        },
        async fetchSizeRuleGroups() {
            const seq = ++this.showSeq;
            try {
                this.loading = true;
                const res = await listRuleGroups({
                    params: {search: RULE_GROUP_SIZE_SEARCH, page: 1, per_page: 100},
                });
                if (seq !== this.showSeq) return;
                const {rule_groups, total} = res.data || {};
                const groups = Array.isArray(rule_groups) ? rule_groups : [];
                this.rows = groups.map(g => {
                    const sizeLabel = extractSizeLabel(g.name);
                    return {
                        ruleGroupId: g.id,
                        ruleGroupName: g.name,
                        sizeLabel,
                        included: true,
                        campaignId: this.autoMatchCampaignId(sizeLabel),
                        result: null,
                        createdId: null,
                    };
                });
                if (total != null && groups.length < total) {
                    // 은폐 금지: 일부만 로드된 경우 사용자에게 알림
                    console.warn(`[preset] 기업 규모 규칙그룹 ${groups.length}/${total}건만 로드됨`);
                    this.$EventBus.$emit('toast-failure', '프리셋', '기업 규모 규칙그룹이 많아 일부만 로드되었습니다. 개발 팀에 문의해주세요.');
                }
            } catch (e) {
                if (seq !== this.showSeq) return;
                console.error(e);
                this.$EventBus.$emit('toast-failure', '프리셋', '기업 규모 규칙그룹을 불러오는 중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
                this.rows = [];
            } finally {
                if (seq === this.showSeq) this.loading = false;
            }
        },
        // 선택된 프로젝트의 기존 규칙셋 → 캠페인별 맵. 사전 중복검사용.
        async fetchExistingRuleSets(projectId) {
            const seq = ++this.existingSeq;
            if (projectId == null) {
                this.existingByCampaign = {};
                return;
            }
            try {
                const res = await listCampaignRuleSets({
                    params: {project_id: projectId, page: 1, per_page: 200},
                });
                if (seq !== this.existingSeq) return;
                const {campaign_rule_sets, total} = res.data || {};
                const list = Array.isArray(campaign_rule_sets) ? campaign_rule_sets : [];
                const map = {};
                list.forEach(rs => {
                    if (rs.campaign_id != null) {
                        map[rs.campaign_id] = {id: rs.id, name: rs.name};
                    }
                });
                this.existingByCampaign = map;
                if (total != null && list.length < total) {
                    console.warn(`[preset] 기존 규칙셋 ${list.length}/${total}건만 로드 — 사전 중복검사가 일부 누락될 수 있음`);
                }
            } catch (e) {
                if (seq !== this.existingSeq) return;
                console.error(e);
                // 사전검사 불가 시 빈 맵 — 서버 409가 최종 방어선
                this.existingByCampaign = {};
            }
        },
        async onSubmit() {
            if (this.submitting) return;
            // 검증·확인창을 기다리는 동안에도 닫기·중복 제출을 막는다
            this.submitting = true;
            let done = false;
            try {
                // 확인창에 보인 대상·값 그대로 만든다(첫 await 전에 고정)
                const form = {...this.form};
                const projectId = this.selectedProjectCodeId;
                const targets = this.includedRows.filter(r => r.result !== 'success' && r.result !== 'conflict');

                const ok = await this.$refs.observer.validate();
                if (!ok || !this.canSubmit) return;

                // 활성 규칙은 만들자마자 배정에 쓰이므로 대상·개수를 확인받는다 (비활성은 바로 진행)
                if (form.is_active) {
                    const h = this.$createElement;
                    const projectText = getTargetProjectText(projectId, this.projects);
                    const lines = targets.map(r => `${form.name} - ${r.sizeLabel} → ${this.campaignText(r.campaignId) || r.campaignId}`);
                    const confirmed = await this.$showModal({
                        type: 'confirm',
                        state: 'warning',
                        title: '활성 규칙셋 생성',
                        message: h('div', {style: {whiteSpace: 'pre-line'}},
                            `프로젝트: ${projectText ?? projectId}\n활성 규칙셋 ${targets.length}개를 만듭니다.\n${lines.join('\n')}\n활성 규칙이 생기면 어떤 규칙에도 맞지 않는 신청자는 배정될 평가지가 없어 신청이 실패할 수 있습니다.`)
                    });
                    if (!confirmed) return;
                }

                this.hasRun = true;
                this.progressDone = 0;
                this.progressTotal = targets.length;
                let createdNow = 0;
                let authLost = false;
                for (const row of targets) {
                    try {
                        const payload = {
                            name: `${form.name} - ${row.sizeLabel}`,
                            description: form.description,
                            is_active: form.is_active,
                            project_id: projectId,
                            campaign_id: row.campaignId,
                            root_rule_group_id: row.ruleGroupId,
                        };
                        const res = await createCampaignRuleSet(payload);
                        this.$set(row, 'result', 'success');
                        this.$set(row, 'createdId', res.data?.id ?? null);
                        createdNow += 1;
                    } catch (e) {
                        // 로그인이 풀리면 남은 행은 보내지 않는다(인터셉터가 로그인으로 보낸다)
                        if (e?.response?.status === 401) {
                            authLost = true;
                            break;
                        }
                        if (e?.response?.status === 409) {
                            this.$set(row, 'result', 'conflict');
                            this.$set(row, 'errData', e.response.data);
                        } else {
                            console.error(e);
                            this.$set(row, 'result', 'error');
                            this.$set(row, 'errMessage', getErrorMessage(e, '') || e?.message || '알 수 없는 오류');
                        }
                    }
                    this.progressDone += 1;
                }

                const included = this.rows.filter(r => r.included);
                const conflictCount = included.filter(r => r.result === 'conflict').length;
                const errorCount = included.filter(r => r.result === 'error').length;
                // 이번 시도에서 하나라도 만들었으면 실패가 섞여도 목록을 갱신한다
                if (createdNow > 0) this.$emit('created');
                if (authLost) return;

                if (errorCount === 0) {
                    // 더 진행할 게 없는 종료 상태 (성공 + 이미 존재).
                    if (conflictCount > 0) {
                        this.$EventBus.$emit('toast-success', '프리셋', `신규 ${createdNow}건 생성, ${conflictCount}건은 이미 존재합니다.`);
                    } else {
                        this.$EventBus.$emit('toast-success', '프리셋', `규칙셋 ${createdNow}개가 생성되었습니다.`);
                    }
                    done = true;
                } else {
                    this.$EventBus.$emit('toast-failure', '프리셋', `이번 시도 ${targets.length}건 중 ${createdNow}건 생성됨. 실패 항목을 확인해주세요.`);
                }
            } finally {
                this.submitting = false;
            }
            // 닫기는 잠금을 푼 뒤에(잠금 중에는 onHide 가 닫기를 막는다)
            if (done) this.close();
        },
        close() {
            this.$bvModal.hide('campaign-rule-set-preset-modal');
        },
    },
};
</script>

<style scoped lang="scss">
.preset-actionbar {
    position: sticky;
    top: 0;
    z-index: 5;
    min-height: 56px;
    // 좌우 음수마진으로 body 패딩(1.5rem) 상쇄 → 모달 전체 너비 차지(full-bleed)
    margin: 0 -1.5rem 16px;
    padding: 12px 1.5rem; // 콘텐츠는 body 콘텐츠와 정렬되도록 좌우 패딩 복원
    background: #fff;
    border-bottom: 1px solid #dee2e6;
}

.matching-card {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;

    .matching-card-header {
        padding: 12px 16px;
        background: #f8f9fa;
        border-bottom: 1px solid #dee2e6;
    }

    .matching-card-body {
        padding: 16px;
    }
}

.preview-box {
    background: #f8f9fa;
    border: 1px dashed #ced4da;
    border-radius: 8px;
    padding: 10px;
    font-size: 13px;

    .preview-row + .preview-row {
        margin-top: 2px;
    }
}
</style>

<style lang="scss">
/* 텔레포트되는 모달이라 전역 클래스로 body 패딩 제어 —
   헤더(액션바)가 body 상단 패딩에 포함되지 않도록(짤림 방지) 상단 패딩 제거 */
.preset-modal {
    .modal-body {
        padding-top: 0;
    }
}
</style>
